/**
 * Configuration des URLs d'API
 * Routes les requêtes vers la bonne API (principale ou secondaire)
 */

import { Capacitor } from '@capacitor/core';
import {
  API_PRIMARY_MOBILE_BOOKING,
  API_SECONDARY_MOBILE_BOOKING,
  isMobileBookingApiConfigured,
} from './mobileApiUrls';

// APIs production (web)
const API_PRIMARY_PROD = 'https://w6khpitahj.execute-api.eu-west-3.amazonaws.com/api';
const API_SECONDARY_PROD = 'https://137jrhqqx9.execute-api.eu-west-3.amazonaws.com/api';

// URLs locales
const API_PRIMARY_LOCAL = 'http://localhost:8000';
const API_SECONDARY_LOCAL = 'http://localhost:8001';

// Routes qui doivent utiliser l'API secondaire (forfaits_bp, events, favorites, etc.)
const SECONDARY_API_ROUTES = [
    '/favorites',
    '/forfaits',
    '/user-forfaits',
    '/forfait', // Ajouté pour /forfait/{id}
    '/get-valid-forfaits',
    '/add-service-to-forfait',
    '/create-forfait',
    '/delete-forfait',
    '/archive-forfait',
    '/purchase-forfait',
    '/confirm-forfait-purchase',
    '/update-forfait',
    '/purchase-credits',
    '/accept-cgv-forfait',
    '/forfait-execution-consent',
    '/accept-cgv-credits',
    '/gift-credits',
    '/gift-vouchers',
    '/my-gift-vouchers',
    '/public/gift-vouchers',
    '/withdraw-credits',
    '/create-subscription-plan',
    '/client-credits',
    '/subscribe',
    '/user-subscription',
    '/sell-credits-pro',
    '/forfaits-for-booking',
    '/update-service-credit-price',
    '/check-client-email',
    '/sell-forfait-offline',
    '/emergency-fix-forfait',
    '/public/calculate-forfait-travel-fees',
    '/calculate-forfait-home-delivery',
    '/user-credits',
    '/user-pro-credits',
    '/user-documents',
    '/documents/view',
    // `/pro/` : forfaits, events, etc. Les packs crédits (payment_bp) sont exclus
    // dans shouldUseSecondaryApi — ils restent sur l’API primaire.
    '/pro/',
    '/pros',
    '/sold-forfaits',

    '/messaging',
    '/upload-url',
    '/conversations',

    '/quotes',
    '/quote',
    '/user/choice',
    '/user/quotes',
    '/user/quote', // accept/reject devis (user connecté) → API secondaire
    '/usable-quote-items',
    '/public/subscribe-to-plan',
    '/public/quote',
    '/public/document-signature',

    '/user-pending-events',
    '/user-events',
    '/get-event',
    '/create-event',
    '/update-event-by-id',
    '/delete-event-by-id',
    '/event/seats',
    '/events',
    '/calendar/events',
    '/calendars',

    '/session-notes',
    '/notes',
    '/animal-notes',
    '/registrations',
    '/sms',
    // Abonnement club SEPA — blueprint enregistré côté secondaire (et primaire pour
    // compatibilité). Aligne le routage sur celui du back-office.
    '/club-subscriptions',
    // boarding → API secondaire (boarding_bp enregistré uniquement sur API_Secondary)
    '/boarding',
    // veterinarians (animal_health_bp) → API secondaire
    '/veterinarians',
    // booking-ask (booking_ask_bp) → API secondaire (plafond Gateway primaire)
    '/booking-ask',
    // animals (animal_controller) → API secondaire
    '/animals',
    // lecture publique carnet partagé (animal_health_bp) → API secondaire
    '/public/animal-carnet',
    // notifications (notification_controller / push tokens) → API secondaire.
    // Utilisé uniquement par l'app native (centre de notifications + push). Le web
    // n'appelle jamais /notifications, donc cet ajout est sans effet côté web.
    '/notifications',
    // Self-service erasure App Store → API secondaire (privacy_erasure_bp)
    '/privacy',
];

/**
 * Détermine si une route doit utiliser l'API secondaire
 */
function shouldUseSecondaryApi(endpoint: string): boolean {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    // payment_bp est sur l'API primaire. Le préfixe `/pro/` enverrait sinon
    // GET /pro/:slug/credit-pack/:packSlug (et /credit-packs) vers le secondaire,
    // qui n'expose pas ces routes (403). Ne pas matcher credit-pack-payment-plans.
    if (
        /\/credit-packs(?:\/|$|\?)/.test(normalizedEndpoint) ||
        /\/credit-pack\/[^/]+/.test(normalizedEndpoint)
    ) {
        return false;
    }
    return SECONDARY_API_ROUTES.some(route => {
        if (normalizedEndpoint === route) return true;
        if (normalizedEndpoint.startsWith(route + '/')) return true;
        if (normalizedEndpoint.startsWith(route + '?')) return true;
        if (route.endsWith('/') && normalizedEndpoint.startsWith(route)) return true;

        return false;
    });
}

/**
 * Hotfix notes Google : le stage prod_mobile_booking renvoie parfois
 * google_rating=null alors que l’API web primaire a les ratings.
 * On route ces endpoints pro-read vers le primary web jusqu’au redeploy mobile.
 */
function shouldUseWebPrimaryForNative(endpoint: string): boolean {
    const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const prefixes = ['/get-pro-by-slug', '/get-pro/', '/search/pros'];
    return prefixes.some(
        (p) => normalized === p || normalized.startsWith(p) || normalized.startsWith(`${p}?`),
    );
}

/**
 * Retourne l'URL de base de l'API appropriée pour un endpoint donné.
 */
export function getApiUrl(endpoint: string = ''): string {
    const useSecondary = shouldUseSecondaryApi(endpoint);
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
        if (shouldUseWebPrimaryForNative(endpoint) && !useSecondary) {
            return API_PRIMARY_PROD;
        }
        if (!isMobileBookingApiConfigured()) {
            console.error(
                '[apiConfig] prod_mobile_booking non configuré — relancez scripts/deploy-prod-mobile-booking-api.sh puis npm run ios:prepare',
            );
            return useSecondary ? API_SECONDARY_PROD : API_PRIMARY_PROD;
        }
        return useSecondary ? API_SECONDARY_MOBILE_BOOKING : API_PRIMARY_MOBILE_BOOKING;
    }

    // ✅ Priorité absolue aux variables d'environnement (Netlify/Vercel)
    const envPrimaryUrl = process.env.REACT_APP_API_BASE_URL;
    const envSecondaryUrl = process.env.REACT_APP_API_SECONDARY_URL;

    if (useSecondary && envSecondaryUrl) {
        return envSecondaryUrl;
    }
    if (!useSecondary && envPrimaryUrl) {
        return envPrimaryUrl;
    }

    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction) {
        // Fallback to hardcoded production URLs if environment variables are not set
        return useSecondary ? API_SECONDARY_PROD : API_PRIMARY_PROD;
    }

    return useSecondary ? API_SECONDARY_LOCAL : API_PRIMARY_LOCAL;
}

/**
 * Construit l'URL complète pour un endpoint
 */
export function buildApiUrl(endpoint: string): string {
    const baseUrl = getApiUrl(endpoint).replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    return `${baseUrl}/${cleanEndpoint}`;
}

/**
 * En-tête pour JWT longue durée côté API (~30 j).
 * Uniquement Capacitor natif — pas le mode dev `?native=1`.
 */
export function getNativeClientHeaders(): Record<string, string> {
    if (!Capacitor.isNativePlatform()) return {};
    return { 'X-Rintintin-Client': 'native' };
}

/** Query param OAuth Google → state signé `rintintin_client: native` (callback sans header custom). */
export function getNativeClientQueryParam(): string {
    return Capacitor.isNativePlatform() ? 'rintintin_client=native' : '';
}
